import { useMemo } from "react";

import { useRealTimeTypedQuery } from "components";
import { typedQueryClient } from "hooks";
import { QUESTION_RESULT_STATUSES } from "models";
import { CarDetailsQueryKeys } from "modules/CarDetails/queryKeys";
import { ApiInstance } from "utils/Api";

import type { DefaultError } from "@tanstack/react-query";
import type { WebSocketMessageListener } from "components";
import type { Appointment, CarCheck } from "models";

type CheckListDataProps = {
  loading: boolean;
  error: DefaultError | null;
  appointments?: Appointment[];
};

const sortAppointments = (appointments: Appointment[]) => appointments.toSorted((a, b) => b.time_car_app.localeCompare(a.time_car_app));

export const getAppointmentHistoryCount = (appointments?: Appointment[]) => appointments?.length ?? 0;

const getChecklistFromCheck = (check: CarCheck) => {
  if (!check.checklist) return;

  let images_count = 0;
  let advised_count = 0;
  let advised_sold_count = 0;
  let necessary_count = 0;
  let necessary_sold_count = 0;

  for (const qi of check.question_items ?? []) {
    images_count += qi.images?.length || 0;
    if (qi.status === QUESTION_RESULT_STATUSES.Advice) {
      advised_count++;
      if (qi.customer_approved) advised_sold_count++;
      continue;
    }
    if (qi.status === QUESTION_RESULT_STATUSES.Necessary) {
      necessary_count++;
      if (qi.customer_approved) necessary_sold_count++;
    }
  }

  return { ...check.checklist, images_count, advised_count, advised_sold_count, necessary_count, necessary_sold_count };
};

const useAppointmentHistoryListeners = (car_id?: number) =>
  useMemo(() => {
    const queryKey = CarDetailsQueryKeys.appointmentHistory(car_id);

    const appointmentHistoryListeners: WebSocketMessageListener<Appointment>[] = [
      {
        model: "Appointment",
        action: "upsert",
        callback: ({ data: appointment }) => {
          if (appointment.car_id !== car_id) return;

          const appointmentHistory = typedQueryClient.getQueryData(queryKey);
          if (!appointmentHistory) return;

          const appIdx = appointmentHistory.findIndex(a => a.id === appointment.id);

          typedQueryClient.setQueryData(
            queryKey,
            appIdx === -1 ? [...appointmentHistory, appointment] : appointmentHistory.with(appIdx, { ...appointmentHistory[appIdx], ...appointment })
          );
        }
      }
    ];

    const checkListeners: WebSocketMessageListener<CarCheck>[] = [
      {
        model: "CarCheck",
        action: "upsert",
        callback: ({ data: check }) => {
          if (check.car_id !== car_id) return;

          const appointmentHistory = typedQueryClient.getQueryData(queryKey);
          if (!appointmentHistory) return;

          const appIdx = appointmentHistory.findIndex(a => a.id === check.appointment_id);
          if (appIdx < 0) return;

          const checklist = getChecklistFromCheck(check);
          if (!checklist) return;

          const appointment = appointmentHistory[appIdx];
          const checklists = appointment.checklists ?? [];
          const checklistIdx = checklists.findIndex(c => c.id === checklist.id);
          const updatedChecklists = checklistIdx > -1 ? checklists.with(checklistIdx, checklist) : [...checklists, checklist];

          typedQueryClient.setQueryData(queryKey, appointmentHistory.with(appIdx, { ...appointment, checklists: updatedChecklists }));
        }
      }
    ];

    return [...appointmentHistoryListeners, ...checkListeners];
  }, [car_id]);

export const useAppointmentHistoryQuery = (car_id?: number): CheckListDataProps => {
  const listeners = useAppointmentHistoryListeners(car_id);

  const {
    data: appointments,
    isFetching,
    error
  } = useRealTimeTypedQuery({
    enabled: car_id !== undefined,
    queryKey: CarDetailsQueryKeys.appointmentHistory(car_id),
    queryFn: async ({ baseUrl, endpoint, params }) => (await ApiInstance.post(endpoint, params, baseUrl)).data.appointments,
    select: sortAppointments,
    listeners
  });

  return { appointments, loading: isFetching, error };
};
